<template>
  <el-drawer
    v-model="visible"
    :direction="direction"
    size="600px"
    :show-close="false"
    :with-header="true"
    append-to-body
    class="message-drawer"
    modal-class="message-drawer-overlay"
    :style="drawerCssVars"
    @open="applyOffsetVars"
    @opened="applyOffsetVars"
    @close="handleClose"
  >
    <template #header>
      <div class="drawer-header">
        <h2 class="drawer-title">官方消息</h2>
        <div class="header-actions">
          <button
            type="button"
            class="action-btn action-btn--primary"
            :disabled="readingAll || !hasUnread"
            @click="handleReadAll"
          >
            一键全部已读
          </button>
          <button
            type="button"
            class="action-btn action-btn--ghost"
            :disabled="clearing || messages.length === 0"
            @click="handleClear"
          >
            一键全部清空
          </button>
        </div>
      </div>
    </template>

    <div
      ref="listRef"
      v-infinite-scroll="loadMore"
      class="message-list"
      :infinite-scroll-disabled="loading || loadingMore || !hasMore"
      :infinite-scroll-distance="80"
    >
      <div
        v-for="item in messages"
        :key="item.messageId"
        class="message-card"
        @click="handleViewDetail(item)"
      >
        <div class="message-icon-wrap">
          <div class="message-icon" :class="isFailedMessage(item) ? 'is-failed' : 'is-success'">
            <svg v-if="isFailedMessage(item)" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M8.2 8.2l7.6 7.6M15.8 8.2l-7.6 7.6"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
            </svg>
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M6.5 12.2l3.6 3.6 7.4-7.6"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <span v-if="item.readStatus === MESSAGE_READ_UNREAD" class="unread-dot"></span>
        </div>

        <div class="message-main">
          <div class="message-title">{{ item.title || '官方消息' }}</div>
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="message-content"
            @click="handleContentClick($event, item)"
            v-html="item.content || ''"
          ></div>
          <!-- eslint-enable vue/no-v-html -->
          <div class="message-time">{{ formatMessageDateTime(item.createDateTime) }}</div>
        </div>

        <button type="button" class="detail-link" @click.stop="handleViewDetail(item)">
          查看详情
          <span class="detail-arrow">›</span>
        </button>
      </div>

      <InfiniteScrollLoader
        :loading="loading || loadingMore"
        :has-more="hasMore"
        :data-length="messages.length"
        :show-empty-state="true"
        :show-back-top="false"
        empty-text="暂无消息"
        image-size="200px"
      />
    </div>
  </el-drawer>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import InfiniteScrollLoader from '@/components/InfiniteScrollLoader.vue'
import {
  MESSAGE_CATEGORY_OFFICIAL,
  MESSAGE_READ_READ,
  MESSAGE_READ_UNREAD,
  messageApi,
  type UserMessageItem,
} from '@/api/message'
import { useMessageDrawerStore } from '@/stores/messageDrawer'
import { useMessageStore } from '@/stores/message'
import { useModalStore } from '@/stores/modal'
import { useUserStore } from '@/stores/user'

type DrawerDirection = 'ltr' | 'rtl' | 'ttb' | 'btt'

const FAILED_TYPE_MARKERS = [
  'FAIL',
  'FAILED',
  'USER_PROFILE_AUDIT_FAILED',
  'USER_CHAT_AUDIT_FAILED',
  'BABY_NOTE_AUDIT_FAILED',
  'AI_ORDER_FAILED',
  'AI_ORDER_PARTIALLY_FAILED',
]

const props = withDefaults(
  defineProps<{
    direction?: DrawerDirection
    /** 紧贴对应屏幕边缘的间距（px），大于 0 时生效 */
    offset?: number
  }>(),
  {
    direction: 'ltr',
    offset: 110,
  }
)

const router = useRouter()
const userStore = useUserStore()
const messageStore = useMessageStore()
const messageDrawerStore = useMessageDrawerStore()
const modalStore = useModalStore()

const listRef = ref<HTMLElement | null>(null)
const loading = ref(false)
const loadingMore = ref(false)
const readingAll = ref(false)
const clearing = ref(false)
const messages = ref<UserMessageItem[]>([])
const pagination = ref({
  page: 1,
  pageSize: 20,
  total: 0,
})

const visible = computed({
  get: () => messageDrawerStore.visible,
  set: (value: boolean) => {
    if (value) messageDrawerStore.open()
    else messageDrawerStore.close()
  },
})

const offsetPx = computed(() => Math.max(0, Number(props.offset) || 0))

const drawerCssVars = computed(() => ({
  '--message-drawer-offset': `${offsetPx.value}px`,
}))

const hasMore = computed(() => messages.value.length < pagination.value.total)

const hasUnread = computed(() =>
  messages.value.some((item) => item.readStatus === MESSAGE_READ_UNREAD)
)

const applyOffsetVars = () => {
  const offset = `${offsetPx.value}px`
  document.documentElement.style.setProperty('--message-drawer-offset', offset)
  document.documentElement.setAttribute('data-message-drawer-direction', props.direction)
  document
    .querySelectorAll<HTMLElement>('.el-drawer.message-drawer, .el-overlay.message-drawer-overlay')
    .forEach((el) => {
      el.style.setProperty('--message-drawer-offset', offset)
      el.setAttribute('data-direction', props.direction)
    })
}

const clearOffsetVars = () => {
  document.documentElement.removeAttribute('data-message-drawer-direction')
}

const formatMessageDateTime = (value?: string | null) => {
  if (!value) return ''
  return value
    .replace('T', ' ')
    .replace(/\+\d{2}:\d{2}$/, '')
    .replace(/\.\d+/, '')
}

const isFailedMessage = (item: UserMessageItem) => {
  const type = (item.messageTypeCode || '').toUpperCase()
  const template = (item.messageTemplateCode || '').toUpperCase()
  if (FAILED_TYPE_MARKERS.some((marker) => type.includes(marker) || template.includes(marker))) {
    return true
  }
  return (item.title || '').includes('失败')
}

const handleClose = () => {
  messageDrawerStore.close()
}

const loadMessages = async (refresh: boolean) => {
  if (!userStore.isLoggedIn) return
  if (refresh) {
    loading.value = true
    pagination.value.page = 1
    messages.value = []
  } else {
    loadingMore.value = true
  }

  try {
    const res = await messageApi.queryMessages({
      categoryCode: MESSAGE_CATEGORY_OFFICIAL,
      page: pagination.value.page,
      pageSize: pagination.value.pageSize,
    })
    if (res?.code !== 0) {
      ElMessage.error(res?.message || '获取消息列表失败')
      return
    }
    const page = res.data
    const list = page?.list || []
    messages.value = refresh ? list : messages.value.concat(list)
    pagination.value.total = page?.total || 0
    pagination.value.page = page?.page || pagination.value.page
    pagination.value.pageSize = page?.pageSize || pagination.value.pageSize
  } catch (error) {
    console.error('获取消息列表失败:', error)
    ElMessage.error('获取消息列表失败')
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

const loadMore = async () => {
  if (loading.value || loadingMore.value || !hasMore.value) return
  pagination.value.page += 1
  await loadMessages(false)
}

const handleReadAll = async () => {
  if (readingAll.value || !hasUnread.value) return
  readingAll.value = true
  try {
    const res = await messageApi.markAllRead(MESSAGE_CATEGORY_OFFICIAL)
    if (res?.code !== 0) {
      ElMessage.error(res?.message || '标记已读失败')
      return
    }
    messages.value = messages.value.map((item) => ({
      ...item,
      readStatus: MESSAGE_READ_READ,
    }))
    void messageStore.fetchCategories()
    ElMessage.success('已全部标记为已读')
  } catch (error) {
    console.error('标记全部已读失败:', error)
    ElMessage.error('标记已读失败')
  } finally {
    readingAll.value = false
  }
}

const handleClear = async () => {
  if (clearing.value || messages.value.length === 0) return
  try {
    await ElMessageBox.confirm('确定清空所有消息通知记录吗？清空后不可恢复', '清空消息确认', {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning',
    })
  } catch {
    return
  }

  clearing.value = true
  try {
    const res = await messageApi.clearMessages()
    if (res?.code !== 0) {
      ElMessage.error(res?.message || '清空消息失败')
      return
    }
    messages.value = []
    pagination.value.page = 1
    pagination.value.total = 0
    void messageStore.fetchCategories()
    ElMessage.success(res.data?.clearedCount ? '消息已清空' : '暂无可清空消息')
  } catch (error) {
    console.error('清空消息失败:', error)
    ElMessage.error('清空消息失败')
  } finally {
    clearing.value = false
  }
}

const markItemRead = async (item: UserMessageItem) => {
  if (item.readStatus === MESSAGE_READ_READ) return
  try {
    const res = await messageApi.markMessageRead(item.messageId)
    if (res?.code !== 0) return
    item.readStatus = MESSAGE_READ_READ
    void messageStore.fetchCategories()
  } catch (error) {
    console.error('标记消息已读失败:', error)
  }
}

const resolveHrefTarget = (raw: string): string | null => {
  const url = raw.trim()
  if (!url) return null
  if (/^(javascript|data|vbscript):/i.test(url)) return null
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith('/pages/')) return null
  if (url.startsWith('/')) return url
  return null
}

const isValidJumpParam = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

const appendJumpParams = (url: string, params?: Record<string, unknown> | null): string => {
  if (!isValidJumpParam(params)) return url
  const pairs: [string, string][] = []
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue
    const valueType = typeof value
    if (valueType !== 'string' && valueType !== 'number' && valueType !== 'boolean') continue
    pairs.push([key, String(value)])
  }
  if (!pairs.length) return url

  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url)
      for (const [key, value] of pairs) {
        parsed.searchParams.set(key, value)
      }
      return parsed.toString()
    } catch {
      return url
    }
  }

  const search = new URLSearchParams(pairs)
  return url.includes('?') ? `${url}&${search.toString()}` : `${url}?${search.toString()}`
}

const resolveJumpTarget = (item: UserMessageItem): string | null => {
  if (typeof item.jumpUrl !== 'string') {
    return item.messageId ? `/message/${item.messageId}` : null
  }
  return resolveHrefTarget(appendJumpParams(item.jumpUrl, item.jumpParam))
}

const navigateToTarget = async (target: string) => {
  if (/^https?:\/\//i.test(target)) {
    window.open(target, '_blank')
    return
  }
  messageDrawerStore.close()
  await nextTick()
  await router.push(target)
}

const getSuccessfulAlgorithmOrderItemId = (item: UserMessageItem): number | null => {
  const ids = item.payload?.successfulAlgorithmOrderItemIds
  if (!Array.isArray(ids) || ids.length === 0) return null
  const id = Number(ids[0])
  if (!Number.isFinite(id) || id <= 0) return null
  return id
}

const goToMembership = async () => {
  messageDrawerStore.close()
  await nextTick()
  if (router.currentRoute.value.path !== '/membership') {
    await router.push('/membership')
    await nextTick()
  }
}

const handleContentClick = async (event: MouseEvent, item: UserMessageItem) => {
  const targetEl = event.target
  if (!(targetEl instanceof Element)) return
  const link = targetEl.closest('a')
  if (!link) return
  event.preventDefault()
  event.stopPropagation()
  await markItemRead(item)
  const target = resolveHrefTarget(link.getAttribute('href') || '')
  if (!target) return
  await navigateToTarget(target)
}

const handleViewDetail = async (item: UserMessageItem) => {
  await markItemRead(item)

  const messageTypeCode = (item.messageTypeCode || '').trim()
  if (messageTypeCode === 'AI_ORDER_SUCCEEDED' || messageTypeCode === 'AI_ORDER_PARTIALLY_FAILED') {
    const algorithmOrderItemId = getSuccessfulAlgorithmOrderItemId(item)
    if (algorithmOrderItemId) {
      await navigateToTarget(
        `/template-detail/${algorithmOrderItemId}?pageType=assets&sourceTab=assets_all`
      )
      return
    }
  } else if (messageTypeCode === 'AI_ORDER_FAILED') {
    await goToMembership()
    modalStore.openInspirationValueModal('earn')
    return
  } else if (messageTypeCode === 'MEMBERSHIP_ACTIVATED') {
    await goToMembership()
    modalStore.openSubscriptionManageModal()
    return
  } else if (messageTypeCode === 'WALLET_RECHARGE_SUCCEEDED') {
    await goToMembership()
    modalStore.openInspirationValueModal('purchase')
    return
  }

  const target = resolveJumpTarget(item)
  if (!target) return
  await navigateToTarget(target)
}

watch(visible, (open) => {
  if (open) {
    applyOffsetVars()
    nextTick(() => applyOffsetVars())
    if (userStore.isLoggedIn) {
      void loadMessages(true)
    } else {
      messages.value = []
      pagination.value.total = 0
    }
  } else {
    clearOffsetVars()
  }
})

watch(
  () => router.currentRoute.value.fullPath,
  (path, oldPath) => {
    if (oldPath && path !== oldPath && visible.value) {
      messageDrawerStore.close()
    }
  }
)
</script>

<style lang="scss">
// 遮罩使用 Element Plus 默认颜色，从间距处开始铺，避免覆盖侧边栏
html[data-message-drawer-direction='ltr'] .el-overlay.message-drawer-overlay,
.el-overlay.message-drawer-overlay[data-direction='ltr'] {
  left: var(--message-drawer-offset, 0px);
}

html[data-message-drawer-direction='rtl'] .el-overlay.message-drawer-overlay,
.el-overlay.message-drawer-overlay[data-direction='rtl'] {
  right: var(--message-drawer-offset, 0px);
}

html[data-message-drawer-direction='ttb'] .el-overlay.message-drawer-overlay,
.el-overlay.message-drawer-overlay[data-direction='ttb'] {
  top: var(--message-drawer-offset, 0px);
}

html[data-message-drawer-direction='btt'] .el-overlay.message-drawer-overlay,
.el-overlay.message-drawer-overlay[data-direction='btt'] {
  bottom: var(--message-drawer-offset, 0px);
}

.el-drawer.message-drawer {
  background: #000000 !important;
  box-shadow: none !important;

  .el-drawer__header {
    margin-bottom: 0 !important;
    padding: 36px 40px 20px !important;
    background: #000000 !important;
    border-bottom: none !important;
  }

  .el-drawer__body {
    padding: 8px 40px 32px !important;
    background: #000000 !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}
</style>

<style lang="scss" scoped>
.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 24px;
}

.drawer-title {
  margin: 0;
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.action-btn {
  height: 36px;
  padding: 0 18px;
  border-radius: 999px;
  font-size: 14px;
  line-height: 36px;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
}

.action-btn--primary {
  border: none;
  color: #ffffff;
  background: #8b6cff;
}

.action-btn--ghost {
  border: 1px solid #3f3f46;
  color: #ffffff;
  background: transparent;
}

.message-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
}

.message-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 24px;
  border-radius: 20px;
  background: #1a1a1a;
  cursor: pointer;
}

.message-icon-wrap {
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
}

.message-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }

  &.is-success {
    background: #7c5cff;
  }

  &.is-failed {
    background: #2a2a2a;
  }
}

.unread-dot {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff6a4c;
}

.message-main {
  flex: 1;
  min-width: 0;
}

.message-title {
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.message-content {
  margin-top: 6px;
  color: #d4d4d8;
  font-size: 14px;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  :deep(p),
  :deep(span),
  :deep(div) {
    display: inline;
    margin: 0;
    padding: 0;
  }

  :deep(a) {
    color: #8b6cff;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}

.message-time {
  margin-top: 8px;
  color: #71717a;
  font-size: 13px;
  line-height: 1.4;
}

.detail-link {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: none;
  background: transparent;
  color: #a1a1aa;
  font-size: 14px;
  cursor: pointer;
}

.detail-arrow {
  font-size: 18px;
  line-height: 1;
}
</style>
